import React from 'react';
import { trackEvent } from '../../utils/analytics/analytics';
import { convertUrlToLanguage } from '../../utils/search-router/url-handling';
import { normalizeSupportedLocale } from '../../utils/routing/locale-from-pathname';
import type { IntlShape } from 'react-intl';
import { LANG_FLAGS } from './LanguageFlags';

export interface LanguageSelectorProps {
  intl: IntlShape;
  formatMessage: IntlShape['formatMessage'];
  /** Current locale; if missing or invalid, falls back to intl.locale so the button is never empty */
  locale?: 'en' | 'fi' | 'sv';
  className?: string;
}

/**
 * Language selector component for navigation.
 * Shows FI/SV/EN as a row of true circles on a white track; the selected language gets its
 * national flag as a 30%-opacity background tint instead of a plain highlight. Matches the SRP
 * header's own language selector (components/search/DesktopFilterCategories.tsx) so the control
 * looks and behaves the same everywhere it appears. Only visible on xl screens and above.
 */
export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  intl,
  formatMessage,
  locale: localeProp,
  className = '',
}) => {
  // Ensure we always have a valid display locale (static SSR may pass undefined; hydration may pass wrong value)
  const locale = normalizeSupportedLocale(localeProp ?? intl?.locale);

  const handleLanguageChange = (newLocale: 'en' | 'fi' | 'sv') => {
    trackEvent({
      name: 'locale_change',
      params: {
        to_locale: newLocale,
        from_locale: locale,
        surface: 'other',
      },
    });
    window.location.href = convertUrlToLanguage(window.location.pathname, newLocale, intl);
  };

  return (
    <div className={`hidden xl:flex items-center gap-1 h-[52px] px-1.5 rounded-full border border-[#DDD5C8]/70 dark:border-[#4A3520]/80 bg-white/90 dark:bg-[#1F1509]/90 ${className}`}>
      {(['fi', 'sv', 'en'] as const).map((lang) => {
        const isActive = locale === lang;
        const Flag = LANG_FLAGS[lang];
        return (
          <button
            key={lang}
            type="button"
            onClick={() => handleLanguageChange(lang)}
            aria-current={isActive ? 'true' : undefined}
            aria-label={formatMessage({
              id: 'navigation.language',
              defaultMessage: 'Language',
              description: 'Button aria-label for language selector in navigation',
            })}
            className="relative w-9 h-9 shrink-0 rounded-full overflow-hidden flex items-center justify-center text-xs font-extrabold uppercase cursor-pointer transition-colors"
          >
            {isActive && <span className="absolute inset-0 opacity-30"><Flag /></span>}
            <span
              className={`relative ${
                isActive
                  ? 'text-[#1E1A14] dark:text-[#F2EADF] [text-shadow:0_0_4px_white,0_0_4px_white,0_0_6px_white] dark:[text-shadow:0_0_4px_#1F1509,0_0_4px_#1F1509,0_0_6px_#1F1509]'
                  : 'text-jc-ink-secondary hover:text-jc-ink'
              }`}
            >
              {lang.toUpperCase()}
            </span>
          </button>
        );
      })}
    </div>
  );
};
